import { useLayoutEffect, useRef } from 'react';

const dialogs = [];
const focusableSelector = 'a[href], area[href], button, input:not([type="hidden"]), select, textarea, [tabindex], [contenteditable="true"]';
const topDialog = () => dialogs[dialogs.length - 1];

const canFocus = (element) => {
  if (!element || element.matches(':disabled') || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
    const style = window.getComputedStyle(ancestor);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
};

const focusableElements = (node) => Array.from(node.querySelectorAll(focusableSelector))
  .filter((element) => element.tabIndex >= 0 && canFocus(element))
  .sort((first, second) => (first.tabIndex || Infinity) - (second.tabIndex || Infinity));

const focusInside = (dialog, preferred) => {
  const target = preferred && dialog.node.contains(preferred) && canFocus(preferred)
    ? preferred
    : focusableElements(dialog.node)[0] || dialog.node;
  target.focus();
};

// Keep keyboard focus in the topmost open dialog, including when its contents
// change to a loading state. Callback updates must not restart focus ownership.
export function useDialogFocus(isOpen, { onClose, canClose = true, initialFocusRef } = {}) {
  const dialogRef = useRef(null);
  const optionsRef = useRef({ onClose, canClose, initialFocusRef });
  optionsRef.current = { onClose, canClose, initialFocusRef };

  useLayoutEffect(() => {
    const node = dialogRef.current;
    if (!isOpen || !node) return;
    const dialog = { node, restoreTarget: document.activeElement, lastFocused: null };
    const nestedIndex = dialogs.findIndex((other) => node.contains(other.node));
    if (nestedIndex < 0) {
      dialogs.push(dialog);
    } else {
      dialog.restoreTarget = dialogs[nestedIndex].restoreTarget;
      dialogs.splice(nestedIndex, 0, dialog);
    }

    const handleFocus = (event) => {
      if (topDialog() !== dialog) return;
      if (node.contains(event.target)) {
        dialog.lastFocused = event.target;
      } else {
        focusInside(dialog, dialog.lastFocused);
      }
    };
    const handleKey = (event) => {
      if (topDialog() !== dialog || event.isComposing) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (optionsRef.current.canClose) optionsRef.current.onClose?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = focusableElements(node);
      const current = document.activeElement;
      const index = controls.indexOf(current);
      if (!controls.length || index < 0 || (event.shiftKey ? index === 0 : index === controls.length - 1)) {
        event.preventDefault();
        (event.shiftKey ? controls[controls.length - 1] || node : controls[0] || node).focus();
      }
    };
    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('focusin', handleFocus);
    if (topDialog() === dialog) focusInside(dialog, optionsRef.current.initialFocusRef?.current);

    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('focusin', handleFocus);
      const wasTop = topDialog() === dialog;
      const index = dialogs.indexOf(dialog);
      if (index !== -1) dialogs.splice(index, 1);
      // If an outer dialog unmounts first, preserve its original opener for
      // the nested dialog's eventual cleanup instead of a detached element.
      dialogs.forEach((other) => {
        if (node.contains(other.restoreTarget)) other.restoreTarget = dialog.restoreTarget;
      });
      if (!wasTop) return;
      const activeDialog = topDialog();
      if (dialog.restoreTarget?.isConnected && canFocus(dialog.restoreTarget)
          && (!activeDialog || activeDialog.node.contains(dialog.restoreTarget))) {
        dialog.restoreTarget.focus();
      } else if (activeDialog) {
        focusInside(activeDialog, activeDialog.lastFocused);
      }
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    const dialog = topDialog();
    if (!isOpen || dialog?.node !== dialogRef.current) return;
    if (!dialog.node.contains(document.activeElement) || !canFocus(document.activeElement)) {
      focusInside(dialog, optionsRef.current.initialFocusRef?.current);
    }
  });

  return dialogRef;
}
